import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { ApiError } from '../../services/BaseApiService'
import { auditorApiService } from '../../services/auditorApiService'
import type { AuditorGrant, AuditorRequest } from '../../services/auditorApiService'
import { colors, fontSize, radius, spacing } from '../../styles/theme'
import AdminTabs from './AdminTabs'
import { Button, useDialogs } from '../../components/ui'

const card: React.CSSProperties = { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: spacing.md }
const btn: React.CSSProperties = { padding: `${spacing.xs} ${spacing.md}`, background: colors.primary, color: '#fff', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }
const ghost: React.CSSProperties = { ...btn, background: 'transparent', color: colors.text, border: `1px solid ${colors.border}` }
const input: React.CSSProperties = { padding: spacing.sm, border: `1px solid ${colors.border}`, borderRadius: radius.sm }
const day = (iso: string | null) => (iso ? iso.slice(0, 10) : '—')

/** Super-admin: grant time-boxed, read-only auditor access and answer auditors' evidence requests. */
export default function AdminAuditorsPage() {
  const dialogs = useDialogs()
  const [grants, setGrants] = useState<AuditorGrant[]>([])
  const [requests, setRequests] = useState<AuditorRequest[]>([])
  const [error, setError] = useState('')
  const [email, setEmail] = useState('')
  const [firm, setFirm] = useState('')
  const [purpose, setPurpose] = useState('SOC 2 Type II examination')
  const [expires, setExpires] = useState(() => new Date(Date.now() + 120 * 864e5).toISOString().slice(0, 10))
  const [inviteErrors, setInviteErrors] = useState<{ email?: string; firm?: string; expires?: string }>({})
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState('')

  async function invite() {
    setSent('')
    const errs: typeof inviteErrors = {}
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errs.email = 'Enter a valid email address.'
    if (!firm.trim()) errs.firm = 'Enter the audit firm.'
    const exp = new Date(`${expires}T23:59:59Z`)
    if (Number.isNaN(exp.getTime()) || exp <= new Date()) errs.expires = 'Choose a date in the future.'
    else if (exp.getTime() - Date.now() > 400 * 864e5) errs.expires = 'Access can last at most 400 days.'
    setInviteErrors(errs)
    if (Object.keys(errs).length) return
    setSending(true); setError('')
    try {
      const res = await auditorApiService.invite(email.trim(), firm.trim(), purpose, `${expires}T23:59:59Z`)
      setSent(res.invited ? `Invite sent to ${email.trim()}.` : `Access granted to existing auditor ${email.trim()}.`)
      setEmail('')
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Invite failed')
    } finally {
      setSending(false)
    }
  }

  const load = useCallback(async () => {
    setError('')
    try {
      const [g, r] = await Promise.all([auditorApiService.listGrants(), auditorApiService.allRequests()])
      setGrants(g); setRequests(r)
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Failed to load') }
  }, [])
  useEffect(() => { load() }, [load])

  async function act(fn: () => Promise<unknown>) {
    setError('')
    try { await fn(); await load() } catch (e) { setError(e instanceof ApiError ? e.message : 'Action failed') }
  }

  const active = (g: AuditorGrant) => !g.revokedAt && new Date(g.expiresAt) > new Date()

  return (
    <>
    <div>
      <AdminTabs />
      <h1 style={{ fontSize: fontSize.xl, fontWeight: 700, color: colors.text, marginBottom: spacing.xs }}>Auditors</h1>
      <p style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginTop: 0 }}>
        Auditors get read-only access to compliance evidence only (no customer data or admin actions), must use MFA, and every
        view and download is logged. Access ends automatically at expiry. <Link to="/audit">Preview the auditor portal</Link>
      </p>
      {error && <div role="alert" style={{ color: colors.error, marginBottom: spacing.sm }}>{error}</div>}

      <div style={{ ...card, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: spacing.sm, marginBottom: spacing.lg }}>
        <Field error={inviteErrors.email}>
          <input style={{ ...input, borderColor: inviteErrors.email ? colors.error : colors.border }} aria-label="Auditor email" aria-invalid={!!inviteErrors.email}
            placeholder="auditor@firm.com" value={email} onChange={e => setEmail(e.target.value)} />
        </Field>
        <Field error={inviteErrors.firm}>
          <input style={{ ...input, borderColor: inviteErrors.firm ? colors.error : colors.border }} aria-label="Audit firm" aria-invalid={!!inviteErrors.firm}
            placeholder="Audit firm" value={firm} onChange={e => setFirm(e.target.value)} />
        </Field>
        <Field><input style={input} aria-label="Purpose" value={purpose} onChange={e => setPurpose(e.target.value)} /></Field>
        <Field error={inviteErrors.expires}>
          <label style={{ fontSize: fontSize.sm }}>Access until <input type="date" aria-label="Access until" value={expires} onChange={e => setExpires(e.target.value)} /></label>
        </Field>
        <div>
          <Button onClick={invite} loading={sending}>{sending ? 'Sending…' : 'Invite auditor'}</Button>
          {sent && <div role="status" style={{ color: colors.success, fontSize: fontSize.sm, marginTop: spacing.xs }}>{sent}</div>}
        </div>
      </div>

      <h2 style={{ fontSize: fontSize.lg, fontWeight: 600 }}>Access grants</h2>
      <div style={{ ...card, padding: 0, overflowX: 'auto', marginBottom: spacing.lg }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fontSize.sm }}>
          <tbody>
            {grants.length === 0 && <tr><td style={{ padding: spacing.sm, color: colors.textSecondary }}>No auditors yet.</td></tr>}
            {grants.map(g => (
              <tr key={g.auditorGrantId} style={{ borderTop: `1px solid ${colors.border}` }}>
                <td style={{ padding: spacing.sm }}><strong>{g.email}</strong><div style={{ color: colors.textSecondary }}>{g.firm}{g.purpose ? ` · ${g.purpose}` : ''}</div></td>
                <td style={{ padding: spacing.sm }}>granted {day(g.grantedAt)}</td>
                <td style={{ padding: spacing.sm, color: active(g) ? colors.success : colors.textSecondary }}>
                  {g.revokedAt ? `revoked ${day(g.revokedAt)}` : active(g) ? `active until ${day(g.expiresAt)}` : `expired ${day(g.expiresAt)}`}
                </td>
                <td style={{ padding: spacing.sm, whiteSpace: 'nowrap' }}>
                  {active(g) && <>
                    <button style={ghost} onClick={async () => {
                      const d = await dialogs.prompt({ title: `Extend ${g.email}'s access`, label: 'New expiry (YYYY-MM-DD)', initialValue: day(g.expiresAt), confirmLabel: 'Extend', required: true })
                      if (d?.trim()) act(() => auditorApiService.extend(g.auditorGrantId, `${d.trim()}T23:59:59Z`))
                    }}>Extend</button>{' '}
                    <button style={{ ...ghost, color: colors.error, borderColor: colors.error }}
                      onClick={async () => { if (await dialogs.confirm({ title: `Revoke ${g.email}'s access now?`, confirmLabel: 'Revoke', danger: true })) act(() => auditorApiService.revoke(g.auditorGrantId)) }}>Revoke</button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 style={{ fontSize: fontSize.lg, fontWeight: 600 }}>Auditor requests</h2>
      {requests.length === 0 && <div style={{ color: colors.textSecondary }}>No requests yet. You are notified when one arrives.</div>}
      {requests.map(r => <RequestCard key={r.auditorRequestId} r={r} onAnswer={(notes, file) => act(() => auditorApiService.answer(r.auditorRequestId, notes, file))} />)}
    </div>
      {dialogs.element}
    </>
  )
}

function RequestCard({ r, onAnswer }: { r: AuditorRequest; onAnswer: (notes: string, file: File | null) => void }) {
  const [notes, setNotes] = useState('')
  const [file, setFile] = useState<File | null>(null)
  return (
    <div style={{ ...card, marginBottom: spacing.sm, borderColor: r.status === 'open' ? colors.warning : colors.border }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <strong>{r.title}</strong>
        <span style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>{r.firm} · {day(r.createdAt)} · {r.status}</span>
      </div>
      {r.details && <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, margin: `${spacing.xs} 0` }}>{r.details}</div>}
      {r.status === 'open' ? (
        <div>
          <textarea aria-label={`Response to ${r.title}`} rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Response notes"
            style={{ width: '100%', boxSizing: 'border-box', padding: spacing.sm }} />
          <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', marginTop: spacing.xs }}>
            <input type="file" aria-label={`Evidence for ${r.title}`} onChange={e => setFile(e.target.files?.[0] ?? null)} />
            <button style={btn} disabled={!notes.trim() && !file} onClick={() => onAnswer(notes, file)}>Send response</button>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: fontSize.sm }}>Answered {day(r.answeredAt)}{r.responseNotes ? `: ${r.responseNotes}` : ''}{r.responseFileName ? ` (${r.responseFileName})` : ''}</div>
      )}
    </div>
  )
}

function Field({ error, children }: { error?: string; children: React.ReactNode }) {
  return (
    <div>
      {children}
      {error && <div role="alert" style={{ color: colors.error, fontSize: fontSize.sm, marginTop: 2 }}>{error}</div>}
    </div>
  )
}
